import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RevoltModal, RevoltButton, RevoltSwitch, RevoltInput, RevoltTabs, RevoltColorPicker } from '../../../../../components';
import JsonViewer from './JsonViewer';

function pickFirstValue(...values) {
  for (const v of values) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string') {
      if (v.trim().length === 0) continue;
      return v;
    }
    return v;
  }
  return null;
}

function extractInsightsOverview(ggufInfo) {
  const general = ggufInfo?.metadata?.general || null;
  const architecture = general?.architecture || null;
  const baseModel0 = general?.base_model?.['0'] || general?.base_model?.[0] || null;

  const base = {
    // Baseline cascades (works even for unknown architectures)
    publisher: pickFirstValue(
      baseModel0?.organization,
      general?.organization,
      general?.publisher
    ),
    displayName: pickFirstValue(
      baseModel0?.name,
      general?.name
    ),
    modelId: pickFirstValue(
      general?.name,
      general?.basename
    ),
    repoUrl: pickFirstValue(
      baseModel0?.repo_url,
      general?.repo_url,
      general?.['repo_url']
    ),
    architecture: architecture,
    ggufVersion: ggufInfo?.version ?? null,
    tensorCount: ggufInfo?.tensorCount ?? ggufInfo?.totalTensorCount ?? null,
    contextLength: null,
    embeddingLength: null,
    blockCount: null,
  };

  /**
   * GGUF `general.architecture` support reference:
   * - `llama`: LLaMA 1/2/3
   * - `mistral`: Mistral, Mixtral
   * - `qwen`: Qwen 1/2
   * - `qwen2`: Qwen2
   * - `qwen3`: Qwen3
   * - `qwen35`: Qwen 3.5
   * - `deepseek`: DeepSeek
   * - `deepseek2`: DeepSeek v2
   * - `gemma`: Gemma
   * - `phi`: Phi-2 / Phi-3
   * - `falcon`: Falcon
   * - `baichuan`: Baichuan
   * - `glm`: ChatGLM / GLM variants
   * - `starcoder`: StarCoder
   * - `gpt_neox`: GPT-NeoX
   * - `mpt`: Mosaic MPT
   * - `yi`: Yi models
   * - `command-r`: Cohere Command
   * - `replit`: Replit code model
   * - `rwkv`: RWKV
   * - `bloom`: BLOOM
   *
   * For each architecture case below, we extract:
   * - `contextLength`
   * - `embeddingLength`
   * - `blockCount`
   * from `ggufInfo.metadata[architecture]` when available.
   */
  switch (architecture) {
    case 'llama': {
      const llama = ggufInfo?.metadata?.llama || null;

      return {
        ...base,
        // Some llama GGUFs provide publisher/name directly on `general` (no `base_model` block).
        // (base already covers this; keep llama override focused on llama-specific sizes)
        contextLength: llama?.context_length ?? null,
        embeddingLength: llama?.embedding_length ?? null,
        blockCount: llama?.block_count ?? null,
      };
    }

    case 'gpt-oss': {
      const gpt = ggufInfo?.metadata?.['gpt-oss'] || null;

      return {
        ...base,
        contextLength: gpt?.context_length ?? null,
        embeddingLength: gpt?.embedding_length ?? null,
        blockCount: gpt?.block_count ?? null,
      };
    }

    case 'qwen3': {
      const qwen = ggufInfo?.metadata?.qwen3 || null;

      return {
        ...base,
        contextLength: qwen?.context_length ?? null,
        embeddingLength: qwen?.embedding_length ?? null,
        blockCount: qwen?.block_count ?? null,
      };
    }

    case 'qwen35': {
      const qwen = ggufInfo?.metadata?.qwen35 || null;

      return {
        ...base,
        contextLength: qwen?.context_length ?? null,
        embeddingLength: qwen?.embedding_length ?? null,
        blockCount: qwen?.block_count ?? null,
      };
    }

    case 'deepseek2': {
      const ds2 = ggufInfo?.metadata?.deepseek2 || null;

      return {
        ...base,
        contextLength: ds2?.context_length ?? null,
        embeddingLength: ds2?.embedding_length ?? null,
        blockCount: ds2?.block_count ?? null,
      };
    }

    default: {
      return base;
    }
  }
}

/**
 * ModelInsightsTab Component
 * Tab content for displaying GGUF model insights
 */
function ModelInsightsTab({ model, isAdmin = false, isFileMissing = false }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState('idle'); // idle | copied | failed
  const [insightsTab, setInsightsTab] = useState('overview');
  const insightsCacheRef = useRef(new Map());
  const currentLoadPathRef = useRef(null);
  const jsonViewerRef = useRef(null);
  const fileMissing = !!isFileMissing || !!model?.fileMissing;

  const withTimeout = useCallback((promise, timeoutMs, timeoutLabel) => {
    let timeoutId = null;
    const timeoutPromise = new Promise((resolve) => {
      timeoutId = setTimeout(() => resolve({ __timedOut: true, label: timeoutLabel }), timeoutMs);
    });
    return Promise.race([promise, timeoutPromise]).finally(() => {
      if (timeoutId) clearTimeout(timeoutId);
    });
  }, []);

  const copyTextToClipboard = useCallback(async (text) => {
    if (!text) return false;

    // Try modern clipboard API first (works in secure contexts)
    try {
      if (navigator?.clipboard?.writeText) {
        const res = await withTimeout(
          navigator.clipboard.writeText(text).then(() => ({ ok: true })),
          800,
          'navigator.clipboard.writeText'
        );
        if (res?.ok) return true;
        // If it hangs (never resolves) in this environment, fall through to other methods.
      }
    } catch (e) {
      // fall through to legacy fallback
    }

    // Legacy fallback: hidden textarea + execCommand('copy')
    try {
      if (typeof document === 'undefined') return false;
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.top = '-1000px';
      el.style.left = '-1000px';
      document.body.appendChild(el);
      el.focus();
      el.select();
      const ok = await withTimeout(
        Promise.resolve(document.execCommand('copy')).then((v) => ({ ok: !!v })),
        300,
        'document.execCommand(copy)'
      );
      document.body.removeChild(el);
      return !!ok?.ok;
    } catch (e) {
      return false;
    }
  }, [withTimeout]);

  const loadInsights = useCallback(async (targetPath = model?.path, { force = false } = {}) => {
    if (!targetPath) return;

    if (fileMissing) {
      setError('Model file is missing. Select a new file in the File tab to view insights.');
      setLoading(false);
      setInsights(null);
      return;
    }

    if (!force) {
      const cachedInsights = insightsCacheRef.current.get(targetPath);
      if (cachedInsights) {
        setInsights(cachedInsights);
        setLoading(false);
        setError(null);
        return;
      }
    }

    if (typeof window === 'undefined' || !window.llamaCppAPI?.getInsights) {
      setError('Model insights are only available in the desktop application');
      setLoading(false);
      return;
    }

    currentLoadPathRef.current = targetPath;
    setLoading(true);
    setError(null);

    try {
      const result = await window.llamaCppAPI.getInsights(targetPath);

      if (currentLoadPathRef.current !== targetPath) return;

      if (result?.success) {
        insightsCacheRef.current.set(targetPath, result);
        setInsights(result);
        if (model?.id && typeof window !== 'undefined' && window.modelsAPI?.setInsightsCache) {
          const overview = extractInsightsOverview(result.metadata);
          if (overview) {
            try { await window.modelsAPI.setInsightsCache(model.id, overview); } catch (_) { /* ignore */ }
          }
        }
      } else {
        setError(result?.error || 'Failed to load insights');
      }
    } catch (err) {
      if (currentLoadPathRef.current !== targetPath) return;
      setError(err?.message || 'Failed to load insights');
    } finally {
      if (currentLoadPathRef.current === targetPath) {
        setLoading(false);
      }
    }
  }, [model, fileMissing]);

  useEffect(() => {
    if (fileMissing) {
      setInsights(null);
      setLoading(false);
      setError('Model file is missing. Select a new file in the File tab to view insights.');
      return;
    }

    if (!model?.path) {
      setInsights(null);
      setLoading(false);
      setError(null);
      return;
    }

    const cachedInsights = insightsCacheRef.current.get(model.path);
    if (cachedInsights) {
      setInsights(cachedInsights);
      setLoading(false);
      setError(null);
      return;
    }

    setInsights(null);

    let frameId = null;
    let timeoutId = null;

    const scheduleLoad = () => {
      loadInsights(model.path);
    };

    // Defer the heavy IPC request until after the tab switch has painted.
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      frameId = window.requestAnimationFrame(() => {
        timeoutId = window.setTimeout(scheduleLoad, 0);
      });
    } else {
      timeoutId = setTimeout(scheduleLoad, 0);
    }

    return () => {
      currentLoadPathRef.current = null;
      if (frameId !== null && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(frameId);
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [model?.path, loadInsights, fileMissing]);

  if (fileMissing) {
    return (
      <div className="model-details-tab-content">
        <div className="model-details-group">
          <h3 className="model-details-group-title">Model Insights</h3>
          <div className="model-insights-error" style={{ padding: '24px 20px' }}>
            <div className="model-insights-icon">
              <i className="fa-solid fa-exclamation-triangle"></i>
            </div>
            <h4>Model File Missing</h4>
            <p>Go to the File tab and point this model to a new GGUF file.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="model-details-tab-content">
        <div className="model-details-group">
          <h3 className="model-details-group-title">Model Insights</h3>
          <div className="model-insights-loading">
            <div className="model-insights-icon">
              <i className="fa-solid fa-spinner fa-spin"></i>
            </div>
            <h4>Analyzing Model...</h4>
            <p>Extracting metadata from GGUF file...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="model-details-tab-content">
        <div className="model-details-group">
          <h3 className="model-details-group-title">Model Insights</h3>
          <div className="model-insights-error">
            <div className="model-insights-icon">
              <i className="fa-solid fa-exclamation-triangle"></i>
            </div>
            <h4>Analysis Failed</h4>
            <p>{error}</p>
            <RevoltButton variant="outline" onClick={() => loadInsights(model?.path, { force: true })}>
              <i className="fa-solid fa-refresh"></i>
              Try Again
            </RevoltButton>
          </div>
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="model-details-tab-content">
        <div className="model-details-group">
          <h3 className="model-details-group-title">Model Insights</h3>
          <div className="model-insights-placeholder">
            <div className="model-insights-icon">
              <i className="fa-solid fa-chart-line"></i>
            </div>
            <h4>GGUF Model Information</h4>
            <p>Technical details about this model will be displayed here, including context size, parameter count, and other metadata extracted from the GGUF file.</p>
            <RevoltButton variant="outline" onClick={() => loadInsights(model?.path, { force: true })}>
              <i className="fa-solid fa-play"></i>
              Analyze Model
            </RevoltButton>
          </div>
        </div>
      </div>
    );
  }

  const gguf = insights?.metadata || null;

  const overviewRaw = insights ? extractInsightsOverview(gguf) : (model?.metadata?.insightsOverview || null);
  const overview = overviewRaw || {};

  return (
    <div className="model-details-tab-content">
      <div className="model-details-group">
        <h3 className="model-details-group-title">Model Insights</h3>
        <RevoltTabs
          tabs={[
            {
              id: 'overview',
              label: 'Overview',
              icon: 'eye',
              content: (
                <div className="model-insights-overview">
                  <div className="model-metadata-grid">
                    <div className="model-info-item">
                      <span className="model-info-label">Publisher</span>
                      <span className="model-info-value">{overview.publisher || '—'}</span>
                    </div>
                    <div className="model-info-item">
                      <span className="model-info-label">Display Name</span>
                      <span className="model-info-value">{overview.displayName || '—'}</span>
                    </div>
                    <div className="model-info-item">
                      <span className="model-info-label">Model ID</span>
                      <span className="model-info-value">{overview.modelId || '—'}</span>
                    </div>
                    <div className="model-info-item">
                      <span className="model-info-label">Repo URL</span>
                      <span className="model-info-value">
                        {overview.repoUrl ? (
                          <a
                            href={overview.repoUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            {overview.repoUrl}
                          </a>
                        ) : (
                          '—'
                        )}
                      </span>
                    </div>
                    <div className="model-info-item">
                      <span className="model-info-label">Architecture</span>
                      <span className="model-info-value">{overview.architecture || '—'}</span>
                    </div>
                    <div className="model-info-item">
                      <span className="model-info-label">GGUF Version</span>
                      <span className="model-info-value">{overview.ggufVersion ?? '—'}</span>
                    </div>
                    <div className="model-info-item">
                      <span className="model-info-label">Tensor Count</span>
                      <span className="model-info-value">{overview.tensorCount ?? '—'}</span>
                    </div>
                    <div className="model-info-item">
                      <span className="model-info-label">Context Length</span>
                      <span className="model-info-value">{overview.contextLength ?? '—'}</span>
                    </div>
                    <div className="model-info-item">
                      <span className="model-info-label">Embedding Length</span>
                      <span className="model-info-value">{overview.embeddingLength ?? '—'}</span>
                    </div>
                    <div className="model-info-item">
                      <span className="model-info-label">Block Count</span>
                      <span className="model-info-value">{overview.blockCount ?? '—'}</span>
                    </div>
                  </div>
                </div>
              )
            },
            ...(isAdmin ? [{
              id: 'admin',
              label: 'Admin',
              icon: 'bug',
              content: (
                <div className="model-insights-raw">
                  <div className="model-insights-header">
                    <h4>Raw GGUF Analysis Data</h4>
                    <p>Debug view for admins. This is the raw JSON payload extracted from the model file.</p>
                  </div>
                  <div className="model-insights-json">
                    <JsonViewer ref={jsonViewerRef} data={insights} />
                  </div>
                  <div className="model-insights-actions">
                    <RevoltButton variant="outline" onClick={() => loadInsights(model?.path, { force: true })}>
                      <i className="fa-solid fa-refresh"></i>
                      Re-analyze
                    </RevoltButton>
                    <RevoltButton
                      variant="outline"
                      onClick={async () => {
                        const snapshotFn = jsonViewerRef.current?.getVisibleSnapshot;
                        if (typeof snapshotFn !== 'function') {
                          setCopyFeedback('failed');
                          setTimeout(() => setCopyFeedback('idle'), 2500);
                          return;
                        }

                        const snapshot = snapshotFn();
                        if (snapshot == null) {
                          setCopyFeedback('failed');
                          setTimeout(() => setCopyFeedback('idle'), 2500);
                          return;
                        }

                        const str = JSON.stringify(snapshot, null, 2);
                        const ok = await copyTextToClipboard(str);
                        if (ok) {
                          setCopyFeedback('copied');
                          setTimeout(() => setCopyFeedback('idle'), 2000);
                          return;
                        }

                        try {
                          window.prompt('Copy JSON (Ctrl+C / Cmd+C, Enter to close):', str);
                        } catch (e) {
                          // ignore
                        }
                        setCopyFeedback('failed');
                        setTimeout(() => setCopyFeedback('idle'), 2500);
                      }}
                    >
                      <i className="fa-solid fa-copy"></i>
                      {copyFeedback === 'copied'
                        ? ' Copied!'
                        : copyFeedback === 'failed'
                          ? ' Copy failed'
                          : ' Copy view as JSON'}
                    </RevoltButton>
                  </div>
                </div>
              )
            }] : [])
          ]}
          activeTab={insightsTab}
          onTabChange={setInsightsTab}
        />
      </div>
    </div>
  );
}

/**
 * ModelDetailsModal Component
 * Modal showing detailed information about a model
 */
function ModelDetailsModal({ isOpen, onClose, model, isAdmin = false, onToggle, onConfigure, onDelete, onUpdatePath, onUpdateName, onUpdateColor }) {
  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [applyingDisplayName, setApplyingDisplayName] = useState(false);

  // Reset state when closing so next open has no tab flicker.
  useEffect(() => {
    if (isOpen) return;
    setActiveTab('overview');
    setEditingName(false);
  }, [isOpen]);

  const handleFileUpdate = async () => {
    // Use Electron dialog for proper absolute path
    if (typeof window !== 'undefined' && window.dialogAPI?.showOpenDialog) {
      try {
        const result = await window.dialogAPI.showOpenDialog({
          title: 'Select GGUF Model File',
          properties: ['openFile'],
          filters: [
            { name: 'GGUF Files', extensions: ['gguf'] },
            { name: 'All Files', extensions: ['*'] }
          ]
        });

        if (result.canceled || !result.filePaths || result.filePaths.length === 0) {
          return;
        }

        const filePath = result.filePaths[0];

        // Create a file-like object with the path
        const fileObject = {
          path: filePath,
          name: filePath.split(/[/\\]/).pop(),
        };

        onUpdatePath?.(model.id, fileObject);
      } catch (error) {
        console.error('Failed to open file dialog:', error);
        alert('Failed to open file dialog. Please try again.');
      }
    } else {
      // Fallback for non-Electron environments
      alert('File selection is only available in the desktop application.');
    }
  };

  const handleUseDisplayName = async () => {
    if (!model?.path) return;
    if (typeof window === 'undefined' || !window.llamaCppAPI?.getInsights) return;

    try {
      setApplyingDisplayName(true);
      const result = await window.llamaCppAPI.getInsights(model.path);
      const overview = extractInsightsOverview(result?.metadata || null);
      const baseModelName = overview?.displayName || null;

      if (!baseModelName) {
        alert('No display name was found in this model metadata.');
        return;
      }

      const nextName = String(baseModelName).trim();
      if (!nextName) {
        alert('Display name in metadata was empty.');
        return;
      }

      if (nextName !== model.displayName) {
        onUpdateName?.(model.id, nextName);
      }

      setTempName(nextName);
      setEditingName(false);
    } catch (e) {
      alert(e?.message || 'Failed to read model metadata.');
    } finally {
      setApplyingDisplayName(false);
    }
  };

  if (!model) return null;

  const isFileMissing = !!model?.fileMissing;

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="model-modal-title">
          <i className="fa-solid fa-brain"></i>
          Model Details
        </div>
      }
      showCloseButton={false}
      closeOnBackdrop={true}
      containerClassName="model-details-modal-container"
    >
      {/* Tab Navigation */}
      <RevoltTabs
        tabs={[
          {
            id: 'overview',
            label: 'Overview',
            icon: 'eye',
            content: (
              <div className="model-details-tab-content">
                <div className="model-details-group">
                  <h3 className="model-details-group-title">Overview</h3>
                  <div className="model-overview-content">
                    {/* First Row: Model Name */}
                    <div className="model-overview-row">
                      <div className="model-overview-item">
                        <span className="model-info-label">Model Name</span>
                        <div className="model-name-row">
                          <div className="model-name-main">
                            {editingName ? (
                              <div className="model-name-edit">
                                <RevoltInput
                                  value={tempName}
                                  onChange={(e) => setTempName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      if (tempName.trim() && tempName !== model.displayName) {
                                        onUpdateName?.(model.id, tempName.trim());
                                      }
                                      setEditingName(false);
                                    } else if (e.key === 'Escape') {
                                      setTempName(model.displayName);
                                      setEditingName(false);
                                    }
                                  }}
                                  onBlur={() => {
                                    if (tempName.trim() && tempName !== model.displayName) {
                                      onUpdateName?.(model.id, tempName.trim());
                                    }
                                    setEditingName(false);
                                  }}
                                  autoFocus
                                />
                              </div>
                            ) : (
                              <div
                                className="model-name-display"
                                onClick={() => {
                                  setTempName(model.displayName);
                                  setEditingName(true);
                                }}
                                title="Click to edit"
                              >
                                <span className="model-name-text">{model.displayName}</span>
                                <i className="fa-solid fa-pencil model-edit-icon"></i>
                              </div>
                            )}
                          </div>

                          <div className="model-name-action">
                            <RevoltButton
                              variant="outline"
                              size="sm"
                              disabled={applyingDisplayName || !model?.path}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleUseDisplayName();
                              }}
                              title="Use display name from GGUF metadata"
                            >
                              <i className="fa-solid fa-wand-magic-sparkles"></i>
                              Use Display Name
                            </RevoltButton>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Color Row: Model Color */}
                    <div className="model-overview-row">
                      <div className="model-overview-item">
                        <div className="model-status-controls" style={{ width: '100%' }}>
                          <span className="model-info-label">Model Color</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end' }}>
                            <RevoltColorPicker
                              value={model?.color || null}
                              onChange={(color) => {
                                onUpdateColor?.(model.id, color);
                              }}
                              disabled={!isAdmin}
                            />
                            <span className="model-info-value" style={{ minWidth: 72, textAlign: 'right' }}>
                              {model?.color || 'Default'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Second Row: Enable/Disable */}
                    <div className="model-overview-row">
                      <div className="model-overview-item">
                        <div className="model-status-controls">
                          <div className="model-details-toggle">
                            <label className="toggle-label">Enable Model</label>
                            <RevoltSwitch
                              checked={model.isActive}
                              disabled={isFileMissing}
                              onChange={() => {
                                if (isFileMissing) return;
                                onToggle(model.id);
                              }}
                              size="default"
                            />
                          </div>
                          <span
                            className={`model-card-status ${
                              isFileMissing ? 'missing-file' : model.isActive ? 'active' : 'inactive'
                            }`}
                          >
                            {isFileMissing ? 'Missing file' : model.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Third Row: Metadata */}
                    <div className="model-overview-row">
                      <div className="model-overview-item">
                        <div className="model-metadata-grid">
                          <div className="model-info-item">
                            <span className="model-info-label">Source</span>
                            <span className="model-info-value">{model.mode === 'url' ? 'URL Download' : 'Local File'}</span>
                          </div>
                          <div className="model-info-item">
                            <span className="model-info-label">Created</span>
                            <span className="model-info-value">{new Date(model.createdAt).toLocaleDateString()}</span>
                          </div>
                          <div className="model-info-item">
                            <span className="model-info-label">Last Modified</span>
                            <span className="model-info-value">{new Date(model.updatedAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          },
          {
            id: 'file',
            label: 'File',
            icon: 'folder',
            content: (
              <div className="model-details-tab-content">
                <div className="model-details-group">
                  <h3 className="model-details-group-title">File Location</h3>
                  {isFileMissing && (
                    <div className="model-insights-error" style={{ padding: '18px 16px', marginBottom: 16 }}>
                      <div className="model-insights-icon">
                        <i className="fa-solid fa-triangle-exclamation"></i>
                      </div>
                      <h4 style={{ marginBottom: 6 }}>Missing GGUF File</h4>
                      <p>Use this tab to point the model to a new GGUF file.</p>
                    </div>
                  )}
                  <div className="model-details-file-row">
                    <div className="model-file-item">
                      <span className="model-info-label">File Path</span>
                      <div className="model-file-path-row">
                        <div className="model-file-path-container">
                          <code className="model-file-path" title={model.path}>
                            {model.path}
                          </code>
                        </div>
                        <RevoltButton
                          variant="outline"
                          size="sm"
                          onClick={handleFileUpdate}
                          title="Browse for new GGUF file"
                        >
                          <i className="fa-solid fa-folder-open"></i>
                        </RevoltButton>
                      </div>
                    </div>
                    <div className="model-file-item">
                      <span className="model-info-label">Model ID</span>
                      <code className="model-id-value">{model.id}</code>
                    </div>
                  </div>
                </div>
              </div>
            )
          },
          {
            id: 'insights',
            label: 'Insights',
            icon: 'chart-line',
            content: (
              <ModelInsightsTab model={model} isAdmin={isAdmin} isFileMissing={!!model?.fileMissing} />
            )
          }
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Actions */}
      <div className="model-details-actions-section">
        <RevoltButton
          variant="danger"
          onClick={() => {
            onDelete(model.id);
            onClose();
          }}
        >
          <i className="fa-solid fa-trash"></i>
          Delete Model
        </RevoltButton>
        <RevoltButton
          variant="secondary"
          onClick={onClose}
        >
          Close
        </RevoltButton>
      </div>
    </RevoltModal>
  );
}

export default ModelDetailsModal;